import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import type { Order } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
  getServerNameFromOrder,
  type CustomerReceiptSettings,
  type ReceiptLoyaltyBlock,
  type UnpaidTenderTotals,
} from '@/lib/printCustomerReceipt'
import { buildReceiptLoyaltyBlock } from '@/lib/buildReceiptLoyaltyBlock'
import { normalizeHex, luma, mix } from '@/lib/walletCardPalette'
import { formatDateDDMMYYYY, formatTimeHuman } from '@/lib/utils'

type Props = {
  /** Fresh order (the caller re-fetches and guards against completed payments). */
  order: Order
  cfg: CustomerReceiptSettings
  /**
   * F-MM-N28: per-tender totals from GET /counter/orders/:id/prebill — the
   * checkout's own recompute. null = tender not offered (card-promo order).
   */
  cash: UnpaidTenderTotals | null
  card: UnpaidTenderTotals | null
  /** Resolved via displayLabelForOrderType — e.g. "Dine-in". */
  orderTypeLabel?: string | null
  onClose: () => void
}

/** 0.15 → "15%", 0.075 → "7.5%", 0 → "". */
function pct(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return ''
  const n = Math.round(fraction * 1000) / 10
  return `${n}%`
}

/**
 * Full-screen customer-facing digital bill — deliberately NOT the printed
 * slip. This is the presentation surface a venue hands across the table:
 * brand-first, generous type, the venue's accent colour throughout. The
 * printed proforma stays one button away (Print Bill) and unchanged.
 *
 * The MONEY is the printed proforma's money: both come from the server's
 * prebill (F-MM-N28), so this screen can never disagree with a printed copy
 * or with what the checkout charges — only look better.
 *
 * No fiscal/FBR block: an unpaid order has no fiscal identity, and a
 * non-final view must never masquerade as a tax invoice. Always light,
 * regardless of the staff theme. Dismissal is staff-only (X / Esc) so the
 * customer holding the tablet can't swipe the bill away.
 */
export function CustomerBillOverlay({
  order,
  cfg,
  cash,
  card,
  orderTypeLabel,
  onClose,
}: Props) {
  const { formatCurrency } = useCurrency()
  const [loyalty, setLoyalty] = useState<ReceiptLoyaltyBlock | null>(null)

  // The loyalty enrolment QR — same builder as the printed slip; failures
  // are swallowed there by design and the block simply doesn't render.
  useEffect(() => {
    let alive = true
    buildReceiptLoyaltyBlock(order.id, { logoUrl: cfg.logoUrl }).then((block) => {
      if (alive) setLoyalty(block)
    })
    return () => {
      alive = false
    }
  }, [order.id, cfg.logoUrl])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  // Brand colour, kept legible whatever hex the tenant configured.
  // walletCardPalette.luma is 0–255 (same scale as palette.go).
  const accent = normalizeHex(cfg.accentColor || '#111827')
  const accentInk = luma(accent) > 150 ? '#1c1917' : '#ffffff'
  const soft = mix(accent, '#ffffff', 0.93)
  const softBorder = mix(accent, '#ffffff', 0.82)

  const items = (order.items ?? []).filter((i) => i.status !== 'voided')

  const discount = order.discount_amount ?? 0
  const itemDiscounts = order.item_discounts_total ?? 0
  // F-MM-N23: server-stamped on the order, never re-derived from settings.
  const marketplaceSettled = order.tax_treatment === 'marketplace_settled'
  // Lines every tender shares come from whichever tender was priced.
  const cashT = (cash ?? card)!
  const cardT = card
  const sameTender = !cash || !card || Math.abs(cash.total - card.total) < 0.005
  const singleLabel = cash && card ? 'Total to pay' : cash ? 'Total to pay (cash)' : 'Total to pay (card)'

  const now = new Date()
  const serverName = getServerNameFromOrder(order)
  const tableNumber = order.table?.table_number ?? ''
  const addressLine = cfg.address
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' · ')

  if (typeof document === 'undefined') return null

  const chips = [
    tableNumber && `Table ${tableNumber}`,
    (order.guest_count ?? 0) > 0 && `${order.guest_count} guests`,
    orderTypeLabel || null,
    serverName && `Served by ${serverName}`,
  ].filter(Boolean) as string[]

  const node = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bill"
      className="fixed inset-0 z-[200] overflow-y-auto"
      style={{
        background: `linear-gradient(160deg, ${mix(accent, '#1c1917', 0.55)} 0%, ${mix(accent, '#0c0a09', 0.85)} 100%)`,
      }}
    >
      {/* Ambient brand glow (customer-facing polish, no assets). */}
      <div
        className="pointer-events-none fixed -left-24 -top-24 h-80 w-80 rounded-full blur-3xl"
        style={{ backgroundColor: mix(accent, '#ffffff', 0.2), opacity: 0.25 }}
        aria-hidden
      />
      <div
        className="pointer-events-none fixed -bottom-24 -right-16 h-72 w-72 rounded-full blur-3xl"
        style={{ backgroundColor: accent, opacity: 0.3 }}
        aria-hidden
      />

      {/* Staff close — deliberately the only dismissal (plus Esc), so the
          customer holding the tablet can't dismiss the bill by touch. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close bill"
        className="fixed right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-stone-500 shadow-md transition-colors hover:text-stone-800"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative mx-auto my-8 w-full max-w-[460px] px-4 sm:my-14">
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_30px_80px_-12px_rgba(0,0,0,0.55)]">
          {/* Brand header */}
          <div
            className="px-8 pb-7 pt-9 text-center"
            style={{ backgroundColor: accent, color: accentInk }}
          >
            {cfg.logoUrl ? (
              <img
                src={cfg.logoUrl}
                alt=""
                className="mx-auto mb-4 max-h-16 w-auto max-w-[55%] object-contain drop-shadow-sm"
              />
            ) : (
              <div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-2xl font-bold shadow-inner"
                style={{ backgroundColor: accentInk, color: accent }}
              >
                {(cfg.businessName || 'R').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="text-[22px] font-semibold tracking-tight">{cfg.businessName}</div>
            {addressLine && <div className="mt-1 text-xs opacity-80">{addressLine}</div>}
            <div className="mt-4 text-[11px] font-medium uppercase tracking-[0.22em] opacity-70">
              Your bill
            </div>
          </div>

          {/* Meta chips */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 px-6 pb-1 pt-5">
            {chips.map((c) => (
              <span
                key={c}
                className="rounded-full px-3 py-1 text-[12px] font-medium text-stone-700"
                style={{ backgroundColor: soft }}
              >
                {c}
              </span>
            ))}
            <span
              className="rounded-full px-3 py-1 text-[12px] font-medium text-stone-700"
              style={{ backgroundColor: soft }}
            >
              {formatDateDDMMYYYY(now)} ·{' '}
              {formatTimeHuman(now)}
            </span>
          </div>

          {/* Items */}
          <div className="px-7 pb-2 pt-5 sm:px-8">
            <div className="space-y-3.5">
              {items.map((i) => {
                const note = (i.special_instructions || '').trim()
                const lineDiscount = Number(i.discount_amount ?? 0)
                return (
                  <div key={i.id} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-lg px-1 text-[12px] font-bold tabular-nums"
                      style={{ backgroundColor: soft, color: mix(accent, '#1c1917', 0.35) }}
                    >
                      {i.quantity}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 text-[15px] font-medium leading-snug text-stone-800">
                          {i.product?.name ?? 'Item'}
                        </span>
                        <span className="shrink-0 text-[15px] tabular-nums text-stone-800">
                          {formatCurrency(Number(i.total_price))}
                        </span>
                      </div>
                      {(i.modifiers ?? []).map((m, idx) => (
                        <div
                          key={idx}
                          className="flex items-baseline justify-between gap-3 text-[13px] text-stone-500"
                        >
                          <span className="min-w-0">{m.option_name}</span>
                          {Number(m.unit_price_delta) !== 0 && (
                            <span className="shrink-0 tabular-nums">
                              {Number(m.unit_price_delta) > 0 ? '+' : '−'}
                              {formatCurrency(
                                Math.abs(Number(m.unit_price_delta)) * (m.quantity || 1) * i.quantity,
                              )}
                            </span>
                          )}
                        </div>
                      ))}
                      {lineDiscount > 0 && (
                        <div className="flex items-baseline justify-between gap-3 text-[13px] text-emerald-600">
                          <span>
                            Discount
                            {typeof i.discount_percent === 'number' && i.discount_percent > 0
                              ? ` (${pct(i.discount_percent / 100)})`
                              : ''}
                          </span>
                          <span className="shrink-0 tabular-nums">−{formatCurrency(lineDiscount)}</span>
                        </div>
                      )}
                      {note && <div className="text-[13px] italic text-stone-400">{note}</div>}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Charges */}
            <div
              className="mt-6 space-y-1.5 border-t pt-4 text-[13.5px] text-stone-500"
              style={{ borderColor: softBorder }}
            >
              <Row label="Subtotal" value={formatCurrency(order.subtotal)} />
              {itemDiscounts > 0 && (
                <Row label="Item discounts" value={`−${formatCurrency(itemDiscounts)}`} green />
              )}
              {discount > 0 && (
                <Row
                  label={`Discount${
                    typeof order.discount_percent === 'number' && order.discount_percent > 0
                      ? ` (${pct(order.discount_percent / 100)})`
                      : ''
                  }`}
                  value={`−${formatCurrency(discount)}`}
                  green
                />
              )}
              {cashT.service > 0 && (
                <Row
                  label={`Service charge${pct(cashT.serviceRate) ? ` (${pct(cashT.serviceRate)})` : ''}`}
                  value={formatCurrency(cashT.service)}
                />
              )}
              {cashT.delivery > 0 && <Row label="Delivery" value={formatCurrency(cashT.delivery)} />}
            </div>

            {/* Totals — one per payment method when the tax rates differ,
                exactly like the printed bill, so the customer sees what each
                way of paying costs before choosing. */}
            <div className="mt-5 space-y-2.5">
              {sameTender ? (
                <TenderCard
                  label={singleLabel}
                  note={
                    marketplaceSettled
                      ? 'Tax settled by marketplace'
                      : pct(cashT.taxRate)
                        ? `incl. ${pct(cashT.taxRate)} tax`
                        : ''
                  }
                  amount={formatCurrency(cashT.total)}
                  accent={accent}
                  accentInk={accentInk}
                  filled
                />
              ) : (
                <>
                  <TenderCard
                    label="Paying by cash"
                    note={pct(cashT.taxRate) ? `incl. ${pct(cashT.taxRate)} tax` : ''}
                    amount={formatCurrency(cashT.total)}
                    accent={accent}
                    accentInk={accentInk}
                    filled
                  />
                  <TenderCard
                    label="Paying by card"
                    note={cardT && pct(cardT.taxRate) ? `incl. ${pct(cardT.taxRate)} tax` : ''}
                    amount={formatCurrency(cardT?.total ?? 0)}
                    accent={accent}
                    accentInk={accentInk}
                  />
                </>
              )}
            </div>
            <p className="mt-3 text-center text-[11px] text-stone-400">
              This is not a tax invoice — your final receipt is issued on payment.
            </p>

            {/* Loyalty — enrolment QR when the programme is configured and the
                visit isn't saved yet; progress when it is. */}
            {loyalty && loyalty.state === 'unsaved' && (
              <div
                className="mt-6 flex items-center justify-between gap-4 rounded-2xl px-5 py-4"
                style={{ backgroundColor: soft }}
              >
                <div className="min-w-0 text-[13.5px] text-stone-700">
                  <div
                    className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                    style={{ color: mix(accent, '#1c1917', 0.25) }}
                  >
                    Save this visit
                  </div>
                  <div className="mt-1 leading-snug">
                    Scan to earn <span className="font-semibold">{loyalty.rewardLabel}</span> on visit{' '}
                    {Math.max(1, Math.round(loyalty.threshold))}.
                  </div>
                </div>
                <div className="shrink-0 rounded-xl bg-white p-2 shadow-sm">
                  <img src={loyalty.qrDataUri} alt="Loyalty QR" className="h-24 w-24" />
                </div>
              </div>
            )}
            {loyalty && loyalty.state === 'saved' && (
              <div
                className="mt-6 rounded-2xl px-5 py-4 text-center text-[13.5px] text-stone-700"
                style={{ backgroundColor: soft }}
              >
                {loyalty.punches >= loyalty.threshold ? (
                  <>
                    Your <span className="font-semibold">{loyalty.rewardLabel}</span> is ready — show
                    your number next visit!
                  </>
                ) : (
                  <>
                    {Math.max(0, loyalty.threshold - loyalty.punches)} more visit
                    {loyalty.threshold - loyalty.punches === 1 ? '' : 's'} to{' '}
                    <span className="font-semibold">{loyalty.rewardLabel}</span>
                  </>
                )}
              </div>
            )}

            {/* Footer */}
            {cfg.thankYouMessage && (
              <p className="mt-7 text-center text-[15px] font-medium text-stone-700">
                {cfg.thankYouMessage}
              </p>
            )}
            <p className="mb-5 mt-2 text-center text-[10px] uppercase tracking-widest text-stone-300">
              powered by bhookly.com
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}

function Row({ label, value, green }: { label: string; value: string; green?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${green ? 'text-emerald-600' : ''}`}>
      <span className="min-w-0">{label}</span>
      <span className="shrink-0 tabular-nums">{value}</span>
    </div>
  )
}

/** Big tender total: filled accent card for the primary, outlined for the alternative. */
function TenderCard({
  label,
  note,
  amount,
  accent,
  accentInk,
  filled,
}: {
  label: string
  note: string
  amount: string
  accent: string
  accentInk: string
  filled?: boolean
}) {
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl px-5 py-4"
      style={
        filled
          ? { backgroundColor: accent, color: accentInk }
          : { border: `1.5px solid ${mix(accent, '#ffffff', 0.6)}`, color: '#1c1917' }
      }
    >
      <div className="min-w-0">
        <div className="text-[13px] font-semibold uppercase tracking-wide">{label}</div>
        {note && <div className="mt-0.5 text-[11px] opacity-75">{note}</div>}
      </div>
      <div className="shrink-0 text-[22px] font-bold tabular-nums tracking-tight">{amount}</div>
    </div>
  )
}
